/**
 * Add-in session time tracking — POST `/contracts/add_in/session_time`.
 * First request: `type: session_start` with `contract_id` + `contract_version_id` (no `session_id`).
 * Subsequent calls: `type: heartbeat` with `session_id`, `elapsed_ms`, and the same contract fields.
 * Backend returns `session_id` on every response — use the latest on the next call (e.g. overlapping days).
 */

import { apiRequest } from "./client";

export const ADD_IN_SESSION_TIME_PATH = "/contracts/add_in/session_time";

export type SessionTimeType = "session_start" | "heartbeat";

export type PostSessionTimePayload =
  | {
      type: "session_start";
      contract_id: string;
      contract_version_id: string;
    }
  | {
      type: "heartbeat";
      session_id: string;
      elapsed_ms: number;
      contract_id: string;
      contract_version_id: string;
    };

/**
 * Reads `session_id` / `sessionId` from a successful POST response body.
 */
export function parseSessionIdFromResponse(raw: unknown): string | undefined {
  if (raw === null || raw === undefined || typeof raw !== "object") {
    return undefined;
  }
  const r = raw as Record<string, unknown>;
  const id = r.session_id ?? r.sessionId;
  return typeof id === "string" && id.trim().length > 0 ? id.trim() : undefined;
}

function bodyFromPayload(payload: PostSessionTimePayload): Record<string, unknown> {
  if (payload.type === "session_start") {
    return {
      type: "session_start",
      contract_id: payload.contract_id,
      contract_version_id: payload.contract_version_id,
    };
  }
  return {
    type: "heartbeat",
    session_id: payload.session_id,
    elapsed_ms: payload.elapsed_ms,
    contract_id: payload.contract_id,
    contract_version_id: payload.contract_version_id,
  };
}

/**
 * Normal POST with Bearer auth. Returns backend `session_id` when present (sent on each response).
 */
export async function postSessionTime(payload: PostSessionTimePayload): Promise<string | undefined> {
  const raw = await apiRequest<unknown>(ADD_IN_SESSION_TIME_PATH, {
    method: "POST",
    jsonBody: bodyFromPayload(payload),
  });
  return parseSessionIdFromResponse(raw);
}
